import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EnterpriseIntegrations = () => {
  const navigate = useNavigate();
  const [hoveredIntegration, setHoveredIntegration] = useState(null);

  const integrations = [
    // Communication
    { name: 'Slack', category: 'Communication', icon: 'MessageSquare', adoption: '96%', status: 'Available' },
    { name: 'Microsoft Teams', category: 'Communication', icon: 'Users', adoption: '96%', status: 'Available' },
    { name: 'Zoom', category: 'Communication', icon: 'Video', adoption: '96%', status: 'Available' },
    { name: 'Google Chat', category: 'Communication', icon: 'MessageCircle', adoption: '96%', status: 'Available' },
    
    // File & Knowledge
    { name: 'Google Drive', category: 'Knowledge', icon: 'FolderOpen', adoption: '92%', status: 'Available' },
    { name: 'SharePoint', category: 'Knowledge', icon: 'FileText', adoption: '92%', status: 'Available' },
    { name: 'OneDrive', category: 'Knowledge', icon: 'Cloud', adoption: '92%', status: 'Available' },
    { name: 'Notion', category: 'Knowledge', icon: 'BookOpen', adoption: '92%', status: 'Available' },
    { name: 'Confluence', category: 'Knowledge', icon: 'Layers', adoption: '92%', status: 'Available' },
    { name: 'Dropbox', category: 'Knowledge', icon: 'Download', adoption: '92%', status: 'Available' },
    
    // CRM / Sales
    { name: 'HubSpot', category: 'CRM', icon: 'TrendingUp', adoption: '89%', status: 'Available' },
    { name: 'Salesforce', category: 'CRM', icon: 'BarChart', adoption: '89%', status: 'Available' },
    { name: 'Pipedrive', category: 'CRM', icon: 'Target', adoption: '89%', status: 'Available' },
    
    // HRIS
    { name: 'Workday', category: 'HRIS', icon: 'Briefcase', adoption: '82%', status: 'Available' },
    { name: 'BambooHR', category: 'HRIS', icon: 'UserCheck', adoption: '82%', status: 'Available' },
    { name: 'HiBob', category: 'HRIS', icon: 'Users', adoption: '82%', status: 'Available' },
    { name: 'Gusto', category: 'HRIS', icon: 'DollarSign', adoption: '82%', status: 'Available' },
    
    // Engineering
    { name: 'Jira', category: 'Engineering', icon: 'CheckSquare', adoption: '78%', status: 'Available' },
    { name: 'Trello', category: 'Engineering', icon: 'Trello', adoption: '78%', status: 'Available' },
    { name: 'Asana', category: 'Engineering', icon: 'List', adoption: '78%', status: 'Available' },
    { name: 'Linear', category: 'Engineering', icon: 'Zap', adoption: '78%', status: 'Available' },
    { name: 'GitHub', category: 'Engineering', icon: 'Github', adoption: '78%', status: 'Available' },
    
    // Documents
    { name: 'PandaDoc', category: 'Documents', icon: 'FileText', adoption: '76%', status: 'Available' },
    { name: 'DocuSign', category: 'Documents', icon: 'Edit', adoption: '76%', status: 'Available' },
    
    // ATS
    { name: 'Greenhouse', category: 'ATS', icon: 'UserPlus', adoption: '74%', status: 'Available' },
    { name: 'Lever', category: 'ATS', icon: 'Briefcase', adoption: '74%', status: 'Available' },
    
    // Additional
    { name: 'Airtable', category: 'Tools', icon: 'Database', adoption: '70%', status: 'Available' },
    { name: 'Monday.com', category: 'Tools', icon: 'Calendar', adoption: '70%', status: 'Available' },
    { name: 'Miro', category: 'Tools', icon: 'Layout', adoption: '70%', status: 'Available' },
    { name: 'Figma', category: 'Tools', icon: 'Figma', adoption: '70%', status: 'Available' },
    
    // SSO
    { name: 'Azure AD', category: 'SSO', icon: 'Shield', adoption: '95%', status: 'Enterprise' },
    { name: 'Okta', category: 'SSO', icon: 'Lock', adoption: '95%', status: 'Enterprise' },
    { name: 'Google Identity', category: 'SSO', icon: 'Key', adoption: '95%', status: 'Enterprise' }
  ];

  return (
    <section className="py-24 bg-[#0B1220]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-white mb-6">
            Built to connect with the tools<br />
            <span className="text-[#FFD100]">your teams already use</span>
          </h2>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            Seamlessly integrate with 50+ enterprise platforms. No migration required.
          </p>
        </div>

        {/* Integrations Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6 mb-12">
          {integrations?.map((integration, index) => (
            <div
              key={index}
              onMouseEnter={() => setHoveredIntegration(index)}
              onMouseLeave={() => setHoveredIntegration(null)}
              className="group relative backdrop-blur-sm bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-[#FFD100]/40 hover:shadow-xl hover:shadow-[#FFD100]/10 transition-all duration-300 cursor-pointer">
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 bg-[#FFD100]/10 rounded-xl flex items-center justify-center group-hover:bg-[#FFD100]/20 group-hover:scale-110 transition-all duration-300">
                  <Icon name={integration?.icon} size={24} color="#FFD100" strokeWidth={2} />
                </div>
                <div className="text-center">
                  <h4 className="text-white font-semibold text-sm mb-1">{integration?.name}</h4>
                  <p className="text-white/50 text-xs">{integration?.category}</p>
                </div>
              </div>

              {/* Hover Tooltip */}
              {hoveredIntegration === index && (
                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 -translate-y-full z-20 backdrop-blur-xl bg-[#0B1220]/95 border border-[#FFD100]/40 rounded-xl p-4 shadow-2xl w-64">
                  <div className="flex items-start gap-3 mb-3">
                    <Icon name={integration?.icon} size={20} color="#FFD100" />
                    <div>
                      <h5 className="text-white font-bold text-sm mb-1">{integration?.name}</h5>
                      <p className="text-white/60 text-xs">{integration?.category}</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-white/60">Enterprise Adoption:</span>
                      <span className="text-[#FFD100] font-semibold">{integration?.adoption}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Status:</span>
                      <span className="text-emerald-400 font-semibold">{integration?.status}</span>
                    </div>
                    <div className="pt-2 border-t border-white/10">
                      <span className="text-white/60">Setup: 1-click installation</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* View All CTA */}
        <div className="text-center">
          <Button
            variant="outline"
            size="lg"
            onClick={() => navigate('/integrations-marketplace')}
            className="border-2 border-[#FFD100]/40 text-[#FFD100] hover:bg-[#FFD100]/10 px-8 py-3">
            View All Integrations →
          </Button>
        </div>
      </div>
    </section>
  );
};

export default EnterpriseIntegrations;